import React from 'react';
import { Table, TableRow } from '@material-ui/core';
import TableComponents from 'components/Table';
import { ExistingPolicyType } from 'store/modules/ExistingPolicies/types';
import { PolicyType } from 'store/modules/Policies/types';
import { ExistingPolicy, RecommendedPolicy } from './Policy';
import {
  sumFunds,
  sumFundInitial,
  sumTransferValue,
  sumTotalOngoing,
  formatCurrency,
  formatPercent
} from 'utils';

interface ExistingPoliciesProps {
  policies: Array<ExistingPolicyType>;
  isEvalue: boolean;
  riskScore: any;
}

interface RecommendedPoliciesProps {
  policies: Array<PolicyType>;
  isEvalue: boolean;
  riskScore: any;
}

export const ExistingPoliciesTable = ({
  policies,
  isEvalue,
  riskScore
}: ExistingPoliciesProps) => {
  const riskHeaders = isEvalue
    ? ['Risk']
    : ['Growth assets', 'Risk comfort score'];
  const sumFundValue = sumFunds(policies);
  const transferValueSum = sumTransferValue(policies);
  const totalOngoingSum = sumTotalOngoing(policies);
  const totalOngoingCharge = totalOngoingSum / sumFundValue;

  return (
    <Table style={{ backgroundColor: '#fff' }}>
      <TableComponents.TableHead
        cells={[
          'Policy owner(s)',
          'Platform',
          'Policy provider',
          'Policy type',
          'Fund value',
          'Transfer value',
          'Total ongoing charge p.a.',
          ...riskHeaders,
          ''
        ]}
      />
      <TableComponents.TableBody>
        {policies.map((policy: ExistingPolicyType, index: number) => {
          return (
            <ExistingPolicy policy={policy} key={index} isEvalue={isEvalue} />
          );
        })}
      </TableComponents.TableBody>
      <TableComponents.TableFooter>
        <TableRow>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell>Overall</TableComponents.TableCell>
          <TableComponents.TableCell>
            {formatCurrency(sumFundValue)}
          </TableComponents.TableCell>
          <TableComponents.TableCell>
            {formatCurrency(transferValueSum)}
          </TableComponents.TableCell>
          <TableComponents.TableCell>
            {formatPercent(totalOngoingCharge ? totalOngoingCharge * 100 : 0)}
          </TableComponents.TableCell>
          {isEvalue ? (
            <TableComponents.TableCell>
              {riskScore || '-'}
            </TableComponents.TableCell>
          ) : (
            <>
              <TableComponents.TableCell>
                {formatPercent(riskScore.growth_score)}
              </TableComponents.TableCell>
              <TableComponents.TableCell>
                {`${riskScore.range?.low} - ${riskScore.range?.high}`}
              </TableComponents.TableCell>
            </>
          )}
        </TableRow>
      </TableComponents.TableFooter>
    </Table>
  );
};

export const RecommendedPoliciesTable = ({
  policies,
  isEvalue,
  riskScore
}: RecommendedPoliciesProps) => {
  const riskHeaders = isEvalue
    ? ['Risk']
    : ['Growth assets', 'Risk comfort score'];
  const sumFundValue = sumFunds(policies);
  const sumFundValueInitial = sumFundInitial(policies);
  const totalOngoingSum = sumTotalOngoing(policies);
  const totalOngoingCharge = totalOngoingSum / sumFundValue;

  return (
    <Table style={{ backgroundColor: '#fff' }}>
      <TableComponents.TableHead
        cells={[
          'Policy owner(s)',
          'Platform',
          'Policy provider',
          'Policy type',
          'Fund value',
          'Annualised contribution',
          'Annualised withdrawal',
          'Total ongoing',
          ...riskHeaders,
          ''
        ]}
      />
      <TableComponents.TableBody>
        {policies.map((policy: PolicyType, index: number) => {
          return (
            <RecommendedPolicy
              policy={policy}
              key={index}
              isEvalue={isEvalue}
            />
          );
        })}
      </TableComponents.TableBody>
      <TableComponents.TableFooter>
        <TableRow>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell>Overall</TableComponents.TableCell>
          <TableComponents.TableCell>
            {formatCurrency(sumFundValueInitial)}
          </TableComponents.TableCell>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell></TableComponents.TableCell>
          <TableComponents.TableCell>
            {formatPercent(totalOngoingCharge ? totalOngoingCharge * 100 : 0)}
          </TableComponents.TableCell>
          {isEvalue ? (
            <TableComponents.TableCell>
              {riskScore || '-'}
            </TableComponents.TableCell>
          ) : (
            <>
              <TableComponents.TableCell>
                {formatPercent(riskScore.growth_score)}
              </TableComponents.TableCell>
              <TableComponents.TableCell>
                {`${riskScore.range?.low} - ${riskScore.range?.high}`}
              </TableComponents.TableCell>
            </>
          )}
        </TableRow>
      </TableComponents.TableFooter>
    </Table>
  );
};
